import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, FileText, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import { EstimatePublicApproval } from '../../types';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const EstimateApprovalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [details, setDetails] = useState<EstimatePublicApproval | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responderName, setResponderName] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [completedAction, setCompletedAction] = useState<'approved' | 'rejected' | null>(null);

  useEffect(() => {
    const loadDetails = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiService.getPublicEstimateApproval(token);
        if (response.success && response.data) {
          setDetails(response.data);
        } else {
          toast.error(response.message || 'Invalid approval link');
        }
      } catch {
        toast.error('Unable to load approval details');
      } finally {
        setIsLoading(false);
      }
    };

    loadDetails();
  }, [token]);

  const isActionable = useMemo(() => {
    if (!details) return false;
    return !details.isExpired && !details.isAlreadyResponded;
  }, [details]);

  const handleApprove = async () => {
    if (!token) return;
    setIsSubmitting(true);
    try {
      const response = await apiService.approveEstimatePublic(token, responderName || undefined);
      if (response.success) {
        setCompletedAction('approved');
        toast.success(response.message || 'Approval recorded');
      } else {
        toast.error(response.message || 'Unable to approve');
      }
    } catch {
      toast.error('Unable to approve');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token || !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.rejectEstimatePublic(token, rejectionReason.trim(), responderName || undefined);
      if (response.success) {
        setCompletedAction('rejected');
        toast.success(response.message || 'Response recorded');
      } else {
        toast.error(response.message || 'Unable to reject');
      }
    } catch {
      toast.error('Unable to reject');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!token || !details) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-6 text-center text-gray-600 dark:text-gray-300">
            This approval link is invalid or has expired.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completedAction === 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-600 mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Thank you</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Your approval has been recorded.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completedAction === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="mx-auto h-12 w-12 text-red-600 mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Response recorded</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">The service provider has been notified.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Estimate Approval Request
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {details.companyName} is requesting your approval for work order{' '}
              <span className="font-semibold">{details.workOrderNumber}</span>.
            </p>
            {details.workOrderTitle && (
              <p className="text-sm text-gray-600 dark:text-gray-300">{details.workOrderTitle}</p>
            )}
            <div className="grid gap-2 text-sm md:grid-cols-2">
              <div><span className="text-gray-500">Client:</span> {details.clientName}</div>
              {details.locationName && <div><span className="text-gray-500">Location:</span> {details.locationName}</div>}
              <div><span className="text-gray-500">Vendor:</span> {details.vendorName}</div>
              <div><span className="text-gray-500">Proposed total:</span> <strong>{formatCurrency(details.totalAmount)}</strong></div>
              {details.nteAmount != null && (
                <div><span className="text-gray-500">NTE:</span> {formatCurrency(details.nteAmount)}</div>
              )}
              {details.amountOverNte != null && details.amountOverNte > 0 && (
                <div><span className="text-gray-500">Over NTE:</span> {formatCurrency(details.amountOverNte)}</div>
              )}
            </div>

            {details.items.length > 0 && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Rate</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.items.map((item) => (
                      <tr key={`${item.id ?? item.description}-${item.sortOrder}`} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(item.rate)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {details.isExpired && (
              <p className="text-sm text-red-600">This approval link has expired. Please contact your service provider.</p>
            )}
            {details.isAlreadyResponded && !details.isExpired && (
              <p className="text-sm text-amber-600">
                This estimate has already been reviewed ({details.clientApprovalStatusLabel}).
              </p>
            )}
          </CardContent>
        </Card>

        {isActionable && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your response</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="responderName">Your name (optional)</Label>
                <Input
                  id="responderName"
                  value={responderName}
                  onChange={(e) => setResponderName(e.target.value)}
                  placeholder="Name of person approving"
                />
              </div>
              <div>
                <Label htmlFor="rejectionReason">Rejection reason (required to reject)</Label>
                <Textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  placeholder="Explain why this estimate is not approved"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleApprove} disabled={isSubmitting}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve Estimate
                </Button>
                <Button variant="outline" onClick={handleReject} disabled={isSubmitting}>
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject Estimate
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EstimateApprovalPage;
